import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import React, { forwardRef, useImperativeHandle } from 'react';
import { usePartnerHotelRedirect } from '../../hooks/usePartnerHotelRedirect';
import { AccommodationCombinedType } from '../Accommodations/Accommodation.types';
import { useHotelBookingGuestSiteTelemetry } from '@apps/guest/routes/HotelBooking/HotelBooking.telemetry';
import { GuestPartnerHotelBookingRedirectParams } from '@apps/guest/routes/HotelBooking/HotelBooking.types';
import { withWindow } from '@shared/utils/withWindow';
import { LinkConfirmationDialog } from '@shared/components/SafeLink/LinkConfirmationDialog';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { RedirectInterceptorDialog } from '@apps/guest/routes/HotelBooking/components/RedirectInterceptorDialog';
import { SearchParams } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

const createUtmParametersForAccommodationsPage = (): SearchParams => {
  return { utm_medium: 'web', utm_campaign: 'accommodations_tile_fast_redirect', utm_source: 'website_accommodations_page' };
};

export interface PartnerHotelRedirectRef {
  handleOnSelectAccommodation: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface PartnerHotelRedirectProps {
  id: string;
  type: AccommodationCombinedType;
  displayName?: Maybe<string>;
  bookingUrl?: Maybe<string>;
  children?: React.ReactNode | undefined;
  onAccommodationsConsoleOpen?: () => void;
}

export const PartnerHotelRedirect = forwardRef<PartnerHotelRedirectRef, PartnerHotelRedirectProps>((props, ref) => {
  const { id, type, displayName, bookingUrl, onAccommodationsConsoleOpen } = props;
  const { isLoading, pricingInfo, redirectState, safeLinkState, interceptorDialogState } = usePartnerHotelRedirect(id, type, bookingUrl);
  const [isAccommodationsConsoleEnabled] = useIsFeatureEnabled('travelAccommodationsConsoleV2');

  const telemetryHotelBooking = useHotelBookingGuestSiteTelemetry();

  /**
   * Trigger telemetry event for hotel and room block
   */
  const triggerTelemetry = useEventCallback((telemetryData: GuestPartnerHotelBookingRedirectParams) => {
    if (type === AccommodationCombinedType.Roomblock) {
      if (telemetryData.roomBlockUrl) {
        telemetryHotelBooking.guestRoomblockHotelRedirect(telemetryData.roomBlockUrl.toString(), telemetryData.roomBlockUrl.hostname, !!telemetryData.roomBlockSuccess);
      } else if (bookingUrl) {
        try {
          const hostname = new URL(bookingUrl).hostname;
          telemetryHotelBooking.guestRoomblockHotelRedirect(bookingUrl, hostname, false);
        } catch {
          telemetryHotelBooking.guestRoomblockHotelRedirect(bookingUrl, '', false);
        }
      }
    } else {
      telemetryHotelBooking.guestPartnerHotelBookingRedirect({
        ...telemetryData,
        ...createUtmParametersForAccommodationsPage(),
      });
    }
  });

  /**
   * Callback for sold-out dialog "Accept" → Redirect to hotelSearchPageUrl
   */
  const handleOnAccept = useEventCallback(() => {
    if (redirectState?.soldOutLink) {
      interceptorDialogState.set(false);
      telemetryHotelBooking.guestPartnerHotelBookingRedirect({ ...redirectState?.soldOutLink?.telemetryData, ...createUtmParametersForAccommodationsPage() });
    }
  });

  /**
   * Callback for sold-out dialog "Decline" → Closes the dialog and stays in accommodations page.
   */
  const handleOnDecline = useEventCallback(() => {
    interceptorDialogState.set(false);
    telemetryHotelBooking.guestPartnerHotelBookingRedirect({ ...redirectState?.normalLink?.telemetryData, ...createUtmParametersForAccommodationsPage() });
  });

  /**
   * Callback for safe link dialog.
   */
  const handleOnClickSafeLink = useEventCallback((redirectToSafeLink: boolean) => {
    if (redirectState?.safeLink) {
      safeLinkState.set(false);
      triggerTelemetry({ ...redirectState.safeLink.telemetryData, redirectToSafeLink });
    }
  });

  const handleOnSelectAccommodation = useEventCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    // Check for if any redirect link is not computed yet, keep existing redirect working until compute redirect link.
    if (isLoading) return;

    if (redirectState?.normalLink && redirectState?.soldOutLink) {
      event.preventDefault();
      if (isAccommodationsConsoleEnabled) {
        onAccommodationsConsoleOpen?.();
      } else {
        withWindow(global => global.open(redirectState?.soldOutLink?.redirectUrl, '_blank'));
      }
      // Telemetry and redirect will happen from dialog accept and reject CTA's
    } else if (redirectState?.safeLink) {
      event.preventDefault();
      safeLinkState.set(true);
      // Telemetry and redirect will happen from dialog proceed and cancel CTA's
    } else if (redirectState?.normalLink?.redirectUrl) {
      event.preventDefault();
      withWindow(global => global.open(redirectState?.normalLink?.redirectUrl, '_blank'));
      triggerTelemetry(redirectState.normalLink.telemetryData);
    }
  });

  const soldOutRedirectUrl =
    redirectState?.normalLink?.redirectUrl && redirectState?.soldOutLink?.redirectUrl ? { redirectUrls: { partnerSearch: redirectState.soldOutLink.redirectUrl.toString() } } : {};

  useImperativeHandle(ref, () => ({
    handleOnSelectAccommodation,
  }));

  return (
    <>
      {safeLinkState.get() && redirectState?.safeLink?.redirectUrl.toString() && (
        <LinkConfirmationDialog
          href={redirectState.safeLink.redirectUrl.toString()}
          isOpen={true}
          onAccept={() => handleOnClickSafeLink(true)}
          onClose={() => handleOnClickSafeLink(false)}
          target={'_blank'}
        />
      )}
      <RedirectInterceptorDialog
        pageContext="AccommodationPlacePartner"
        isOpen={interceptorDialogState.get() && !!soldOutRedirectUrl.redirectUrls}
        hotel={{
          hotelName: displayName,
          hotelPriceCurrency: pricingInfo?.nightlyBaseRate.currency,
          hotelPrice: pricingInfo?.nightlyBaseRate.floatingPointDecimalString,
          hotelStrikeOutPrice: pricingInfo?.strikeoutNightlyBaseRate?.floatingPointDecimalString,
        }}
        {...soldOutRedirectUrl}
        onAccept={handleOnAccept}
        onDecline={handleOnDecline}
      />
    </>
  );
});
